import { useState, useEffect } from 'react';
import { logErrorUnlessNetworkFailure } from 'setup/logger/logErrorUnlessNetworkFailure';
import { logError } from 'setup/logger/logError';
import { isAbortError } from 'shared/utils/errorHandling';
import { redactUrlQueryForLog } from 'shared/utils/signedUrlFileUtils';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { PDF_PREVIEW_LOAD_TIMEOUT_MS } from '../../app/constants/filePreview';
import { pdfPreviewService } from 'services/fileService/pdfPreviewService';

interface UsePdfDocumentResult {
  loading: boolean;
  error: boolean;
  pdfDocument: PDFDocumentProxy | null;
  numPages: number;
}

export const usePdfDocument = (
  src: string | undefined
): UsePdfDocumentResult => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(
    null
  );
  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    if (!src) {
      setError(true);
      setPdfDocument(null);
      setNumPages(0);
      setLoading(false);
      return;
    }

    setPdfDocument(null);
    setNumPages(0);

    let isMounted = true;
    const abortController = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let hasTimedOut = false;
    let loadingTask:
      | {
          promise: Promise<PDFDocumentProxy>;
          destroy: () => Promise<void> | void;
        }
      | null = null;

    const loadPdf = async (): Promise<void> => {
      timeoutId = setTimeout(() => {
        if (isMounted) {
          hasTimedOut = true;
          abortController.abort();
          if (loadingTask) {
            void loadingTask.destroy();
          }
          logError('PDF document loading timeout', {
            pdfSrc: redactUrlQueryForLog(src),
            timeoutMs: PDF_PREVIEW_LOAD_TIMEOUT_MS,
          });
          setError(true);
          setLoading(false);
        }
      }, PDF_PREVIEW_LOAD_TIMEOUT_MS);

      try {
        setLoading(true);
        setError(false);

        loadingTask = await pdfPreviewService.createPreviewLoadingTask(
          src,
          abortController.signal
        );
        const pdf = await loadingTask.promise;

        if (!isMounted || hasTimedOut) {
          pdf.cleanup();
          pdf.destroy();
          return;
        }

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        if (isMounted && !hasTimedOut) {
          setPdfDocument(pdf);
          setNumPages(pdf.numPages);
          setLoading(false);
        }
      } catch (err) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        if (isMounted && !hasTimedOut) {
          if (isAbortError(err)) {
            return;
          }

          logErrorUnlessNetworkFailure('Error loading PDF document:', err, {
            pdfSrc: redactUrlQueryForLog(src),
          });
          setError(true);
          setLoading(false);
        }
      }
    };

    void loadPdf();

    return () => {
      isMounted = false;
      abortController.abort();
      if (loadingTask) {
        void loadingTask.destroy();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [src]);

  return { loading, error, pdfDocument, numPages };
};
